import React, { Fragment } from 'react';
import './slideover.css';
import { Dialog, Transition } from '@headlessui/react';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { CloseIconButton, Label } from '@nooz-optics/nooz-components-lib';
import { IButton, ILink, ILabel } from '../../../../utils/types/contentful';
import { sidebarState } from '../../../../utils/recoil/sidebar';
import { Button } from '../../Elements';

export interface SidebarProps {
  actionButton?: IButton;
  title?: ILabel;
  button: IButton;
  backdropLink?: ILink;
  body: JSX.Element;
  footer: JSX.Element;
  onClose: Function;
  placement: string;
  isOpen: boolean;
}

export const Sidebar = () => {
  const [sidebar, setSidebar] = useRecoilState(sidebarState);
  const resetSidebar = useResetRecoilState(sidebarState);

  const handleOnCloseClick = () => {
    resetSidebar();
    if (sidebar.onClose) {
      sidebar.onClose();
    }
  };

  const returnTransitionsParam = () => {
    const defaultTransition = {
      enterFrom: 'translate-x-full',
      enterTo: 'translate-x-0',
      leaveFrom: 'translate-x-0',
      leaveTo: 'translate-x-full',
    };

    switch (sidebar.placement) {
      case 'right':
        return defaultTransition;
      case 'left':
        return {
          enterFrom: '-translate-x-full',
          enterTo: 'translate-x-0',
          leaveFrom: 'translate-x-0',
          leaveTo: '-translate-x-full',
        };
      case 'bottom':
        return {
          enterFrom: 'translate-y-full',
          enterTo: 'translate-y-0',
          leaveFrom: 'translate-y-0',
          leaveTo: 'translate-y-full',
        };
      case 'top':
        return {
          enterFrom: '-translate-y-full',
          enterTo: 'translate-y-0',
          leaveFrom: 'translate-y-0',
          leaveTo: '-translate-y-full',
        };
      default:
        return defaultTransition;
    }
  };

  const sidebarWidth = () => {
    if (sidebar.placement === 'top' || sidebar.placement === 'bottom') {
      return 'w-full';
    } else if (sidebar.width) {
      return sidebar.width || '';
    } else {
      return sidebar.width || '';
    }
  };

  const sidebarHeight =
    (sidebar.placement === 'left' || sidebar.placement === 'right') &&
    'inset-y-0';

  return (
    <>
      <Transition.Root
        show={sidebar.show}
        as={Fragment}>
        <Dialog
          as='div'
          static
          className='fixed inset-0 overflow-hidden z-50'
          open={sidebar.show}
          onClose={() => setSidebar({ ...sidebar, show: false })}>
          <div className='absolute inset-0 overflow-hidden'>
            {sidebar.backdropLink && (
              <Transition.Child
                as={Fragment}
                enter='ease-in-out duration-100'
                enterFrom='opacity-0'
                enterTo='opacity-100'
                leave='ease-in-out duration-100'
                leaveFrom='opacity-100'
                leaveTo='opacity-0'>
                <div className='hidden xl:block absolute left-1/4 top-1/2 z-50 pointer-events-none'>
                  <Label
                    text={sidebar.backdropLink?.text}
                    color={{ classname: 'primary' }}
                    size='lg'
                    decoration='underline'
                  />
                </div>
              </Transition.Child>
            )}
            <Transition.Child
              as={Fragment}
              enter='ease-in-out duration-100'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in-out duration-100'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'>
              <Dialog.Overlay className='absolute cursor-pointer inset-0 bg-gray-300 bg-opacity-75 backdrop-filter  backdrop-blur-sm transition-opacity'>
                <div
                  className='w-full h-full'
                  onClick={handleOnCloseClick}
                />
              </Dialog.Overlay>
            </Transition.Child>
            <div
              className={`fixed ${sidebarHeight} ${sidebar.placement}-0 max-w-full max-h-full flex`}>
              <Transition.Child
                as={Fragment}
                enter='transform transition ease-in-out duration-300'
                leave='transform transition ease-in-out duration-300'
                {...returnTransitionsParam()}>
                <div
                  className={`bg-white flex ${sidebarWidth()} flex-col h-full justify-between`}>
                  <div className='flex flex-col h-full overflow-auto grow-0 no-scrollbar'>
                    <div className='flex flex-shrink-0 w-full items-center justify-between px-6 py-3'>
                      {/* Close button */}
                      <CloseIconButton onClick={handleOnCloseClick} />
                      {/* Title */}
                      {sidebar.title && (
                        <div className='pl-3'>
                          <Label
                            {...sidebar.title}
                            color={{ classname: 'gray-800' }}
                            size='2xl'
                            weight='bold'
                          />
                        </div>
                      )}
                      {/* Action button */}
                      {sidebar.actionButton}
                    </div>
                    {/* Sub Header */}
                    {sidebar.subHeader && sidebar.subHeader}
                    {/* Body */}
                    {sidebar.body && (
                      <div className='overflow-y-auto grow-0 flex items-start no-scrollbar'>
                        {sidebar.body}{' '}
                      </div>
                    )}
                  </div>
                  {/* Footer */}
                  {sidebar.footer && (
                    <div className='flex-shrink-0'>{sidebar.footer}</div>
                  )}
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      {/* Close button */}
    </>
  );
};
